import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBackRounded, ArrowForwardRounded, AutoAwesomeOutlined, NoteAddOutlined } from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { useAIRequests } from '../context/AIRequestContext';
import { useUsage } from '../context/UsageContext';
import { hasReachedLimit } from '../services/usageService';
import edgeFunctionService from '../services/edgeFunctionService';
import projectContextService from '../services/projectContextService';
import { AI_TONES } from '../constants/aiTones';
import { analyzeTemplateQuality } from '../utils/templateQualityScore';
import { parseTemplateFields } from '../utils/templateFields';
import { openDocumentEditor, projectPath } from '../utils/navigation';
import ExportDropdown from './ExportDropdown';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import { PageContainer, PageHeader, SectionCard } from './pagekit';
import { isPresentationDocument, isPresentationTemplateType, variantDocumentName } from './documents/documentMeta';
import TemplateStep from './documentGenerator/TemplateStep';
import SourcesStep, { plural } from './documentGenerator/SourcesStep';
import ReviewStep from './documentGenerator/ReviewStep';
import GenerationResult from './documentGenerator/GenerationResult';
import useGenerationRequest from './documentGenerator/useGenerationRequest';
import { parseIdListParam, pickRequestedIds } from './documentGenerator/generatorParams';

const STEP_LABELS = ['Choose template', 'Choose sources', 'Review and generate'];
const STEP_LABELS_SHORT = ['Template', 'Sources', 'Review'];
const DEFAULT_TONE = 'The Scholar';

/**
 * Whether a project has anything to generate from.
 * @param {object} project - Project.
 * @returns {boolean} True with requirements, sessions or documents.
 */
const hasSources = (project) =>
  (project.requirements?.length || 0) + (project.sessions?.length || 0) + (project.generatedDocuments?.length || 0) > 0;

/**
 * Preview HTML for a template: document templates as-is (rendered with AI fields), decks as
 * their slides one after another.
 * @param {object|null} template - Template.
 * @returns {string} HTML.
 */
function templatePreviewHtml(template) {
  if (!template) return '';
  if (!isPresentationTemplateType(template.templateType)) return template.content || '';
  try {
    const parsed = JSON.parse(template.content || '{}');
    const slides = Array.isArray(parsed.slides) ? parsed.slides : [];
    return slides.map((slide, index) => `<h3>Slide ${index + 1}</h3>${slide.content || ''}<hr>`).join('') || '<p>This deck has no slides yet.</p>';
  } catch {
    return '<p>This deck could not be previewed.</p>';
  }
}

/**
 * Selected ids to send: undefined when everything is selected (the server then uses all, including
 * items added later), else the explicit list.
 * @param {Set<string>} selected - Selected ids.
 * @param {number} total - Available items.
 * @returns {string[]|undefined} Ids or undefined.
 */
const selectionPayload = (selected, total) => (selected.size === total ? undefined : Array.from(selected));

/**
 * Document generation: a 3-step flow (template -> sources -> review) that queues an async
 * generation (ai_requests + generate-document edge function -> worker) and then tracks it,
 * showing per-field status and offering "Regenerate" with the next variant.
 * Query params (see documentGenerator/generatorParams.js): `templateId`, `projectId`,
 * `customerId` preselect; `requirementIds` / `sessionIds` (comma lists) narrow the project's
 * evidence to those items, e.g. from "Generate document from these" on the Requirements page.
 * @returns {JSX.Element} Page.
 */
const DocumentGenerator = memo(() => {
  const { state, actions } = useApp();
  const { createRequest, cancelRequest } = useAIRequests();
  const { refreshUsage } = useUsage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [customerFilterId, setCustomerFilterId] = useState('');
  const [requirementIds, setRequirementIds] = useState(() => new Set());
  const [sessionIds, setSessionIds] = useState(() => new Set());
  const [contentSource, setContentSource] = useState('requirements');
  const [selectedExistingDocumentId, setSelectedExistingDocumentId] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [selectedTone, setSelectedTone] = useState(DEFAULT_TONE);
  const [selectedProjectFileCount, setSelectedProjectFileCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [documentLimitReachedOpen, setDocumentLimitReachedOpen] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [resultPreview, setResultPreview] = useState(null);
  /** The submitted run: { requestId, variant, baseName, documentName, projectId, templateId }. */
  const [run, setRun] = useState(null);

  // Blank document dialog state
  const [blankDocDialogOpen, setBlankDocDialogOpen] = useState(false);
  const [blankDocCustomer, setBlankDocCustomer] = useState('');
  const [blankDocProject, setBlankDocProject] = useState('');
  const [creatingBlankDoc, setCreatingBlankDoc] = useState(false);
  const [blankDocError, setBlankDocError] = useState('');
  /** Note about evidence preselected from the link (?requirementIds / ?sessionIds). */
  const [linkNotice, setLinkNotice] = useState(null);

  const generation = useGenerationRequest(run?.requestId || null);

  // The worker inserts the generated_documents row server-side and nothing pushes it into
  // AppContext. Once per finished document (each regenerated variant too), add the loaded row to
  // AppContext so Documents, the project tab and the "Use an existing document" list include it.
  // When that is not possible (the document could not be loaded here, or its project is not in
  // AppContext) reload the projects instead.
  const completedDocumentId = generation.request?.status === 'completed' ? generation.request?.result_data?.documentId || null : null;
  const syncedDocumentIdRef = useRef(null);
  const refreshProjectsPromiseRef = useRef(null);
  useEffect(() => {
    const doc = generation.document;
    const documentId = doc?.id || (generation.error ? completedDocumentId : null);
    if (!documentId || syncedDocumentIdRef.current === documentId) return;
    syncedDocumentIdRef.current = documentId;
    refreshProjectsPromiseRef.current = null;
    if (doc?.id && actions.upsertGeneratedDocumentLocal({ ...doc, project_id: doc.project_id || run?.projectId })) return;
    refreshProjectsPromiseRef.current = Promise.resolve(actions.loadProjects()).catch((error) => {
      console.warn('Could not refresh projects after generation:', error);
    });
  }, [generation.document, generation.error, completedDocumentId, actions, run?.projectId]);

  const project = useMemo(() => state.projects.find((p) => p.id === selectedProject) || null, [state.projects, selectedProject]);
  const template = useMemo(() => state.templates.find((t) => t.id === selectedTemplate) || null, [state.templates, selectedTemplate]);
  const isPresentation = isPresentationTemplateType(template?.templateType);

  const qualityById = useMemo(() => {
    const scores = {};
    state.templates.forEach((t) => {
      if (!isPresentationTemplateType(t.templateType)) scores[t.id] = analyzeTemplateQuality(t.content || '');
    });
    return scores;
  }, [state.templates]);

  const templateFields = useMemo(
    () => (template && !isPresentation ? parseTemplateFields(template.content || '').fields : []),
    [template, isPresentation]
  );

  const customerFilter = useMemo(
    () => (customerFilterId ? state.customers.find((c) => c.id === customerFilterId) || null : null),
    [state.customers, customerFilterId]
  );

  const projectOptions = useMemo(
    () => state.projects.filter((p) => !customerFilterId || p.customer_id === customerFilterId),
    [state.projects, customerFilterId]
  );

  /**
   * Documents usable as the reference. Document templates: documents generated from the same
   * template. Decks: the project's Word documents (the recommended source for slides) plus decks
   * generated from the same template.
   */
  const existingDocuments = useMemo(() => {
    const docs = project?.generatedDocuments || [];
    if (!selectedTemplate) return docs;
    if (!isPresentation) return docs.filter((doc) => doc.templateId === selectedTemplate);
    return docs.filter((doc) => doc.templateId === selectedTemplate || !isPresentationDocument(doc, state.templates));
  }, [project, selectedTemplate, isPresentation, state.templates]);

  // Changing the template can drop the chosen reference from the list: clear it so the sources
  // step asks again instead of sending a document the list no longer offers.
  useEffect(() => {
    if (!selectedExistingDocumentId) return;
    if (!existingDocuments.some((doc) => doc.id === selectedExistingDocumentId)) setSelectedExistingDocumentId('');
  }, [existingDocuments, selectedExistingDocumentId]);

  /**
   * Select a project and default its evidence to everything.
   * @param {string} projectId - Project id ('' clears).
   * @returns {void}
   */
  const selectProject = useCallback((projectId) => {
    setSelectedProject(projectId);
    const next = state.projects.find((p) => p.id === projectId);
    setRequirementIds(new Set((next?.requirements || []).map((r) => r.id)));
    setSessionIds(new Set((next?.sessions || []).map((s) => s.id)));
    setSelectedExistingDocumentId('');
    setContentSource('requirements');
  }, [state.projects]);

  // Apply ?templateId / ?projectId / ?customerId / ?requirementIds / ?sessionIds once, when the
  // data they refer to is loaded.
  const paramsAppliedRef = useRef(false);
  useEffect(() => {
    if (paramsAppliedRef.current) return;
    const templateId = searchParams.get('templateId');
    const projectId = searchParams.get('projectId');
    const customerId = searchParams.get('customerId');
    const linkedRequirementIds = parseIdListParam(searchParams.get('requirementIds'));
    const linkedSessionIds = parseIdListParam(searchParams.get('sessionIds'));
    const needsTemplates = Boolean(templateId) && state.templates.length === 0;
    const needsProjects = Boolean(projectId || customerId) && state.projects.length === 0 && state.customers.length === 0;
    if ((needsTemplates || needsProjects) && !state.initialLoadComplete) return;
    // A project named in the link may still be loading (projects load after customers).
    if (projectId && !state.initialLoadComplete && !state.projects.some((p) => p.id === projectId)) return;
    paramsAppliedRef.current = true;

    if (customerId && state.customers.some((c) => c.id === customerId)) setCustomerFilterId(customerId);
    const projectRow = projectId ? state.projects.find((p) => p.id === projectId) : null;
    if (projectRow && hasSources(projectRow)) {
      selectProject(projectRow.id);
      const pickedRequirements = pickRequestedIds(linkedRequirementIds, projectRow.requirements);
      const pickedSessions = pickRequestedIds(linkedSessionIds, projectRow.sessions);
      if (pickedRequirements) setRequirementIds(pickedRequirements);
      if (pickedSessions) setSessionIds(pickedSessions);
      const requested = linkedRequirementIds.length + linkedSessionIds.length;
      if (requested) {
        const picked = (pickedRequirements?.size || 0) + (pickedSessions?.size || 0);
        const parts = [];
        if (pickedRequirements) parts.push(plural(pickedRequirements.size, 'requirement'));
        if (pickedSessions) parts.push(plural(pickedSessions.size, 'session'));
        if (!picked) {
          setLinkNotice({ severity: 'warning', text: 'The items you selected are no longer in this project, so all of its evidence is selected.' });
        } else {
          const skipped = requested - picked;
          setLinkNotice({
            severity: skipped ? 'warning' : 'info',
            text: `Using the ${parts.join(' and ')} you selected${skipped ? ` (${plural(skipped, 'item')} no longer in the project ${skipped === 1 ? 'was' : 'were'} skipped)` : ''}. You can change this in Choose sources.`,
          });
        }
      }
    }
    if (templateId && state.templates.some((t) => t.id === templateId)) {
      setSelectedTemplate(templateId);
      setActiveStep(1);
    }
  }, [searchParams, state.templates, state.projects, state.customers, state.initialLoadComplete, selectProject]);

  // Suggested name until the user edits it.
  useEffect(() => {
    if (nameTouched || !project || !template) return;
    setDocumentName(`${project.name} - ${template.name}`);
  }, [project, template, nameTouched]);

  useEffect(() => {
    let active = true;
    if (!selectedProject) {
      setSelectedProjectFileCount(0);
      return () => {
        active = false;
      };
    }
    projectContextService
      .listProjectContextFiles(selectedProject)
      .then((rows) => {
        if (active) setSelectedProjectFileCount(rows.length);
      })
      .catch((error) => {
        console.warn('Could not load selected project file count:', error);
        if (active) setSelectedProjectFileCount(0);
      });
    return () => {
      active = false;
    };
  }, [selectedProject]);

  const requirementTotal = project?.requirements?.length || 0;
  const sessionTotal = project?.sessions?.length || 0;

  /** Why the sources step cannot continue yet ('' when it can). */
  const sourcesBlocker = useMemo(() => {
    if (!project) return 'Choose a project.';
    if (contentSource === 'existing_document') {
      return selectedExistingDocumentId ? '' : 'Choose the reference document.';
    }
    if (requirementTotal + sessionTotal === 0) return 'This project has no requirements or sessions yet.';
    if (requirementTotal > 0 && requirementIds.size === 0) return 'Select at least one requirement.';
    if (sessionTotal > 0 && sessionIds.size === 0) return 'Select at least one session.';
    return '';
  }, [project, contentSource, selectedExistingDocumentId, requirementTotal, sessionTotal, requirementIds, sessionIds]);

  const evidenceLabel = useMemo(() => {
    if (contentSource === 'existing_document') {
      const doc = existingDocuments.find((d) => d.id === selectedExistingDocumentId);
      return `Reference document: ${doc?.name || 'not selected'}`;
    }
    const parts = [];
    if (requirementTotal > 0) {
      parts.push(requirementIds.size === requirementTotal
        ? `All ${plural(requirementTotal, 'requirement')}`
        : `${requirementIds.size} of ${plural(requirementTotal, 'requirement')}`);
    }
    if (sessionTotal > 0) {
      parts.push(sessionIds.size === sessionTotal
        ? `all ${plural(sessionTotal, 'session')}`
        : `${sessionIds.size} of ${plural(sessionTotal, 'session')}`);
    }
    return parts.join(', ') || 'No evidence selected';
  }, [contentSource, existingDocuments, selectedExistingDocumentId, requirementTotal, requirementIds, sessionTotal, sessionIds]);

  const canContinue = [Boolean(template), !sourcesBlocker, Boolean(documentName.trim())][activeStep];

  /**
   * Queue a generation with the current selections.
   * @param {number} variant - 0 for the first run; regenerate passes the next number.
   * @param {string} baseName - Document name without a variant suffix.
   * @returns {Promise<void>}
   */
  const startGeneration = async (variant, baseName) => {
    if (!project || !template || !baseName.trim()) return;
    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'documents')) {
      setDocumentLimitReachedOpen(true);
      return;
    }

    setSubmitting(true);
    setGenerationError('');
    const name = variantDocumentName(baseName.trim(), variant);
    const usingRequirements = contentSource !== 'existing_document';
    const existingDocumentId = usingRequirements ? null : selectedExistingDocumentId;
    const selectedRequirementIds = selectionPayload(requirementIds, requirementTotal);
    const selectedSessionIds = selectionPayload(sessionIds, sessionTotal);
    let aiRequest = null;
    try {
      aiRequest = await createRequest({
        type: 'document',
        title: name,
        projectId: project.id,
        templateId: template.id,
        priority: 0,
        data: {
          tone: selectedTone,
          contentSource,
          existingDocumentId,
          variant,
          ...(selectedRequirementIds ? { requirementIds: selectedRequirementIds } : {}),
          ...(selectedSessionIds ? { sessionIds: selectedSessionIds } : {}),
        },
      });

      await edgeFunctionService.generateDocument({
        requestId: aiRequest.id,
        projectId: project.id,
        templateId: template.id,
        documentName: name,
        tone: selectedTone,
        contentSource,
        existingDocumentId,
        requirementIds: selectedRequirementIds,
        sessionIds: selectedSessionIds,
        variant,
      });

      refreshUsage();
      setRun({
        requestId: aiRequest.id,
        variant,
        baseName: baseName.trim(),
        documentName: name,
        projectId: project.id,
        templateId: template.id,
      });
    } catch (error) {
      console.error('Error starting document generation:', error);
      // The request row was created but never dispatched: don't leave a phantom job running.
      if (aiRequest?.id) {
        try {
          await cancelRequest(aiRequest.id);
        } catch (cancelError) {
          console.warn('Could not cancel the undispatched request:', cancelError);
        }
      }
      setGenerationError(error.message || 'Failed to start document generation');
      setRun(null);
      setActiveStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setRun(null);
    setActiveStep(0);
    setSelectedTemplate('');
    selectProject('');
    setCustomerFilterId('');
    setDocumentName('');
    setNameTouched(false);
    setSelectedTone(DEFAULT_TONE);
    setGenerationError('');
    setLinkNotice(null);
    navigate({ search: '' }, { replace: true });
  };

  /**
   * Creates a blank document linked to the selected project and opens it in the editor.
   * @returns {Promise<void>}
   */
  const handleCreateBlankDocument = async () => {
    if (!blankDocProject) return;
    setCreatingBlankDoc(true);
    setBlankDocError('');
    try {
      const result = await actions.addGeneratedDocument({
        projectId: blankDocProject,
        name: 'New Document',
        content: '<p></p>',
        templateId: null,
        templateName: null,
        requirementsCount: 0,
      });
      if (!result.success) throw new Error(result.error || 'Failed to create document');
      setBlankDocDialogOpen(false);
      setBlankDocCustomer('');
      setBlankDocProject('');
      openDocumentEditor(navigate, { documentId: result.data.id, projectId: blankDocProject, title: 'New Document' });
    } catch (error) {
      setBlankDocError(error.message || 'Failed to create blank document');
    } finally {
      setCreatingBlankDoc(false);
    }
  };

  const openGenerated = (doc) => {
    if (!doc?.id) return;
    openDocumentEditor(navigate, {
      documentId: doc.id,
      projectId: run?.projectId || project?.id,
      templateId: run?.templateId || null,
      title: doc.name || run?.documentName,
    });
  };

  const stepLabels = narrow ? STEP_LABELS_SHORT : STEP_LABELS;
  const exportFileBase = (resultPreview?.name || run?.documentName || 'document').replace(/[^a-z0-9]+/gi, '_').toLowerCase();

  return (
    <PageContainer sx={{ maxWidth: 1040 }}>
      <PageHeader
        title="Generate a document"
        description="Pick a template, choose the evidence, and Inky fills the template's AI fields. Everything else is copied exactly as written."
        actions={(
          <Button
            variant="outlined"
            startIcon={<NoteAddOutlined />}
            onClick={() => {
              setBlankDocError('');
              setBlankDocCustomer(project?.customer_id || customerFilterId || '');
              setBlankDocProject(project?.id || '');
              setBlankDocDialogOpen(true);
            }}
          >
            Blank document
          </Button>
        )}
      />

      <SectionCard>
        {run ? (
          <GenerationResult
            request={generation.request}
            document={generation.document}
            loadError={generation.error}
            documentName={run.documentName}
            variant={run.variant}
            templateFields={templateFields}
            busy={submitting}
            onOpen={openGenerated}
            onPreview={(doc) => doc && setResultPreview(doc)}
            onRegenerate={() => startGeneration(run.variant + 1, run.baseName)}
            onRetry={() => startGeneration(run.variant, run.baseName)}
            onEditInputs={() => {
              setRun(null);
              setActiveStep(2);
            }}
            onReset={handleReset}
            onViewProject={async () => {
              // Make sure the project's Documents tab lists the new document: finish a running
              // refresh, or start one if the document never reached AppContext.
              const synced = Boolean(completedDocumentId) && syncedDocumentIdRef.current === completedDocumentId;
              const pending = refreshProjectsPromiseRef.current
                || (completedDocumentId && !synced ? Promise.resolve(actions.loadProjects()) : null);
              if (pending) {
                try {
                  await pending;
                } catch (error) {
                  console.warn('Could not refresh projects before opening the project:', error);
                }
              }
              navigate(projectPath(run.projectId, 'documents'));
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {linkNotice && activeStep < 2 && (
              <Alert severity={linkNotice.severity} onClose={() => setLinkNotice(null)}>
                {linkNotice.text}
              </Alert>
            )}
            <Stepper activeStep={activeStep} nonLinear={false} sx={{ '& .MuiStepLabel-label': { fontSize: 13.5 } }}>
              {stepLabels.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  {index < activeStep ? (
                    <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
                  ) : (
                    <StepLabel>{label}</StepLabel>
                  )}
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <TemplateStep
                templates={state.templates}
                qualityById={qualityById}
                value={selectedTemplate}
                onChange={(id) => {
                  setSelectedTemplate(id);
                  setSelectedExistingDocumentId('');
                }}
                onPreview={setTemplatePreview}
                onCreateTemplate={() => navigate('/templates')}
              />
            )}

            {activeStep === 1 && (
              <SourcesStep
                projects={projectOptions}
                project={project}
                onProjectChange={selectProject}
                customerFilter={customerFilter}
                onClearCustomerFilter={() => setCustomerFilterId('')}
                fileCount={selectedProjectFileCount}
                requirementIds={requirementIds}
                onRequirementIdsChange={setRequirementIds}
                sessionIds={sessionIds}
                onSessionIdsChange={setSessionIds}
                contentSource={contentSource}
                onContentSourceChange={setContentSource}
                existingDocuments={existingDocuments}
                existingDocumentId={selectedExistingDocumentId}
                onExistingDocumentChange={setSelectedExistingDocumentId}
                isPresentation={isPresentation}
              />
            )}

            {activeStep === 2 && (
              <ReviewStep
                template={template}
                quality={template ? qualityById[template.id] : undefined}
                project={project}
                evidenceLabel={evidenceLabel}
                fileCount={selectedProjectFileCount}
                documentName={documentName}
                onDocumentNameChange={(value) => {
                  setNameTouched(true);
                  setDocumentName(value);
                }}
                tone={selectedTone}
                onToneChange={setSelectedTone}
                tones={AI_TONES}
                error={generationError}
                onEditStep={setActiveStep}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                pt: 2,
                borderTop: '1px solid var(--q-border-subtle)',
              }}
            >
              {activeStep > 0 && (
                <Button startIcon={<ArrowBackRounded />} onClick={() => setActiveStep((s) => s - 1)} disabled={submitting}>
                  Back
                </Button>
              )}
              {activeStep === 1 && sourcesBlocker && project && (
                <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }} role="status">{sourcesBlocker}</Box>
              )}
              <Box sx={{ flex: 1 }} />
              {activeStep < 2 ? (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRounded />}
                  onClick={() => setActiveStep((s) => s + 1)}
                  disabled={!canContinue}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlined />}
                  onClick={() => startGeneration(0, documentName)}
                  disabled={!canContinue || submitting || Boolean(sourcesBlocker) || !template}
                >
                  {submitting ? 'Starting…' : 'Generate document'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </SectionCard>

      {/* Template preview (rendered, with AI fields highlighted) */}
      <DocumentPreviewDialog
        open={Boolean(templatePreview)}
        onClose={() => setTemplatePreview(null)}
        title={templatePreview?.name || 'Template preview'}
        subtitle={isPresentationTemplateType(templatePreview?.templateType) ? 'PowerPoint template' : 'Template preview'}
        mode={isPresentationTemplateType(templatePreview?.templateType) ? 'document' : 'template'}
        content={templatePreviewHtml(templatePreview)}
        actions={templatePreview && (
          <Button
            variant="contained"
            onClick={() => {
              setSelectedTemplate(templatePreview.id);
              setTemplatePreview(null);
            }}
          >
            Use this template
          </Button>
        )}
      />

      {/* Generated document preview with export */}
      <DocumentPreviewDialog
        open={Boolean(resultPreview)}
        onClose={() => setResultPreview(null)}
        title={resultPreview?.name || run?.documentName || 'Generated document'}
        subtitle={project?.name || ''}
        content={resultPreview?.content || ''}
        actions={resultPreview && (
          <>
            <ExportDropdown
              variant="outlined"
              htmlContent={resultPreview.content}
              fileName={`${exportFileBase}.${isPresentation ? 'pptx' : 'docx'}`}
              projectName={project?.name || resultPreview.name}
              buttonText={isPresentation ? 'Export to PowerPoint' : 'Export'}
              enablePptx={isPresentation}
            />
            <Button
              variant="contained"
              onClick={() => {
                const doc = resultPreview;
                setResultPreview(null);
                openGenerated(doc);
              }}
            >
              Open in editor
            </Button>
          </>
        )}
      />

      {/* New Blank Document Dialog */}
      <Dialog
        open={blankDocDialogOpen}
        onClose={() => !creatingBlankDoc && setBlankDocDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New blank document</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose the project it belongs to. It opens in the editor as "New Document" so you can start writing right away.
          </DialogContentText>
          {blankDocError && <Alert severity="error" sx={{ mb: 2 }}>{blankDocError}</Alert>}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="blank-doc-customer-label">Customer</InputLabel>
            <Select
              labelId="blank-doc-customer-label"
              value={blankDocCustomer}
              label="Customer"
              onChange={(e) => {
                setBlankDocCustomer(e.target.value);
                setBlankDocProject('');
              }}
            >
              {state.customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!blankDocCustomer}>
            <InputLabel id="blank-doc-project-label">Project</InputLabel>
            <Select
              labelId="blank-doc-project-label"
              value={blankDocProject}
              label="Project"
              onChange={(e) => setBlankDocProject(e.target.value)}
            >
              {state.projects
                .filter((p) => !blankDocCustomer || p.customer_id === blankDocCustomer)
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlankDocDialogOpen(false)} disabled={creatingBlankDoc}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateBlankDocument}
            disabled={!blankDocProject || creatingBlankDoc}
            startIcon={creatingBlankDoc ? <CircularProgress size={16} color="inherit" /> : <NoteAddOutlined />}
          >
            {creatingBlankDoc ? 'Creating…' : 'Create and open'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Limit Reached Dialog */}
      <Dialog open={documentLimitReachedOpen} onClose={() => setDocumentLimitReachedOpen(false)}>
        <DialogTitle>Document limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've reached your monthly document limit. Upgrade your plan to generate more documents.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentLimitReachedOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setDocumentLimitReachedOpen(false);
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
});

DocumentGenerator.displayName = 'DocumentGenerator';

export default DocumentGenerator;
